export interface TeamCreationIntent {snapshotId: string; employeeKeys?: string[]; teamName?: string; amount?: number; teamId?: string; selection?: {mode: 'all_matching'; criteria: import('./workforce-team-criteria.state').WorkforceCriteria; revision?: number}; }
export interface TeamCreationProgress {
  purpose?: 'create' | 'add_members';
  preflight?: {complete: boolean; [key: string]: number | boolean};
  operationId: string; phase: 'preflight' | 'preparing' | 'needs_review' | 'team_committed' | 'complete' | 'closed';
  selectedCount: number; preparedCount: number; memberCount: number; teamId: string | null;
  issues: string[]; message: string | null;
}
export interface TeamCreationApi {
  submitWorkforceTeam(companyId: string, request: TeamCreationIntent, key: string): Promise<TeamCreationProgress>;
  getWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress>;
  recoverWorkforceTeamCreations(companyId: string): Promise<{operations: TeamCreationProgress[]}>;
  continueWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress>;
  closeWorkforceTeamCreation(companyId: string, operationId: string): Promise<TeamCreationProgress>;
}
export class WorkforceTeamCreationState {
  companyId = '';
  progress: TeamCreationProgress | null = null;
  recoverable: TeamCreationProgress[] = [];
  busy = false;
  error = '';
  private version = 0;
  private actionKey: string | null = null;
  private request: TeamCreationIntent | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private wake: (() => void) | null = null;
  constructor(private api: TeamCreationApi,
              private onComplete: (teamId: string) => void = () => {},
              private makeKey: () => string = () => Array.from(crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join('')) {}
  get progressMessage(): string {
    const p = this.progress;
    if (!p) return '';
    if (p.message) return p.message;
    if (p.phase === 'complete') return p.purpose === 'add_members' ? 'Employee additions completed.' : 'Team created.';
    if (p.phase === 'closed') return 'Recovery closed. Prepared accounts and committed memberships are retained.';
    if (p.phase === 'preflight') return 'Checking the complete intended cohort before preparing accounts.';
    if (p.phase === 'needs_review') return 'Review required. Account preparation may be partially completed; membership has not committed.';
    return 'Preparing selected accounts.';
  }
  stop(): void {
    ++this.version; this.busy = false;
    if (this.timer) { clearTimeout(this.timer); }
    this.timer = null; this.wake?.(); this.wake = null;
  }
  destroy(): void { this.stop(); this.progress = null; this.recoverable = []; this.request = null; this.actionKey = null; }
  async open(companyId: string): Promise<void> {
    this.destroy(); this.companyId = companyId; this.error = '';
    const version = this.version;
    try {
      const result = await this.api.recoverWorkforceTeamCreations(companyId);
      if (version === this.version) { this.recoverable = result.operations.slice(0, 10); }
    } catch (_) { if (version === this.version) { this.error = 'Unable to check unfinished team creation. Reload to resolve access.'; } }
  }
  async submit(request: TeamCreationIntent): Promise<void> {
    if (this.busy || this.progress && !['complete', 'closed'].includes(this.progress.phase)) { throw new Error('Resume the current team creation first.'); }
    const serialized = JSON.stringify(request);
    if ((!request.selection && (!request.employeeKeys?.length || request.employeeKeys.length > 100)) || new TextEncoder().encode(serialized).byteLength > 65536) { throw new Error('Selection is outside the temporary V1 limits.'); }
    if (this.request && JSON.stringify(this.request) !== serialized) { throw new Error('Resolve the previous submission before changing its details.'); }
    const submission: TeamCreationIntent = JSON.parse(serialized);
    this.request = submission; this.actionKey = this.actionKey || this.makeKey();
    this.progress = null; this.busy = true; this.error = '';
    const version = this.version; const company = this.companyId;
    try {
      const progress = await this.api.submitWorkforceTeam(company, submission, this.actionKey);
      if (version !== this.version) { return; }
      this.progress = progress; this.actionKey = null; this.request = null;
      this.busy = false;
      void this.resume(progress.operationId);
    } catch (error: any) {
      if (version === this.version) {
        const code = error?.data?.code || error?.error?.code;
        const rejected: Record<string, string> = {
          preflight_blocked: 'A bounded identity check could not be completed. Review the account matches before submitting again.',
          criteria_invalid: 'Choose supported inclusion criteria or all eligible employees.',
          criteria_changed: 'Saved criteria changed. Close this uncommitted operation and review the current revision.',
          criteria_required: 'Save membership criteria before adding newly matching employees.',
          team_busy: 'Another membership operation is active for this team. Resume it or wait for it to finish.',
          team_not_managed: 'This command is available only for snapshot-created teams.',
          request_invalid: 'Check the team details and the temporary 100-employee limit.',
          authority_changed: 'The selected snapshot or connection is no longer authorized. Reload the workforce and review the selection.',
          projection_unavailable: 'A selected employee lacks required onboarding evidence. Remove that employee or request a new enriched snapshot.',
          employee_ineligible: 'A selected employee is no longer eligible for team creation.',
          primary_email_required: 'A selected new employee needs a primary email before onboarding.',
          identity_conflict: 'Selected employee emails match more than one account. Resolve the match before creating the team.',
          brand_conflict: 'A selected account belongs to a conflicting company brand. Resolve it before creating the team.',
        };
        if (rejected[code] && [400, 409, 422].includes(error?.status)) {
          this.request = null; this.actionKey = null; this.error = rejected[code];
          throw new Error(this.error);
        }
        this.error = 'Submission could not be confirmed. Retry with the same details, or reload to find and resume the operation.';
        throw new Error(this.error);
      }
    } finally { if (version === this.version && !this.progress) { this.busy = false; } }
  }
  private delay(): Promise<void> {
    return new Promise(resolve => { this.wake = resolve; this.timer = setTimeout(() => {
      this.timer = null; this.wake = null; resolve();
    }, 750); });
  }
  async resume(operationId: string): Promise<void> {
    if (this.busy) { return; }
    const version = this.version; const company = this.companyId;
    this.busy = true; this.error = '';
    try {
      let progress = await this.api.getWorkforceTeamCreation(company, operationId);
      if (version !== this.version) { return; }
      this.progress = progress;
      // One loop, one POST at a time; every response is fenced to this company/view lifetime.
      for (; !['complete', 'closed'].includes(progress.phase);) {
        if (version !== this.version) { return; }
        try { progress = await this.api.continueWorkforceTeamCreation(company, operationId); }
        catch (error: any) {
          if (error?.status === 403 || error?.status === 401) { throw error; }
          progress = await this.api.getWorkforceTeamCreation(company, operationId);
          // Recover a lost POST response without starting a second team or an overlapping loop.
        }
        if (version !== this.version) { return; }
        this.progress = progress;
        if (progress.issues.length || progress.phase === 'needs_review') { break; }
        if (!['complete', 'closed'].includes(progress.phase)) { await this.delay(); }
      }
      if (progress.phase === 'complete' && progress.teamId) {
        this.recoverable = this.recoverable.filter(item => item.operationId !== operationId);
        this.onComplete(progress.teamId);
      }
    } catch (_) {
      if (version === this.version) { this.error = 'Account setup is paused. Resolve access or connection changes, then Resume. Completed preparation is retained.'; }
    } finally { if (version === this.version) { this.busy = false; } }
  }
  async close(): Promise<void> {
    if (this.busy || !this.progress) { return; }
    const version = this.version;
    try {
      const value = await this.api.closeWorkforceTeamCreation(this.companyId, this.progress.operationId);
      if (version === this.version) { this.progress = value; this.recoverable = this.recoverable.filter(item => item.operationId !== value.operationId); }
    } catch (_) { if (version === this.version) { this.error = 'The operation is still active. Wait for its current work to finish before closing recovery.'; } }
  }
}
